import React, { useState } from 'react';
import { 
  HelpCircle, BookOpen, Gamepad2, Trophy, Award, Users, 
  Settings, ClipboardList, FolderOpen, Layers, Mic, Search,
  ChevronDown, ChevronUp, AlertTriangle, Info, MapPin, 
  Activity, Sparkles, FileText, ShoppingBag
} from 'lucide-react';
import './UserGuide.css';

const UserGuide = ({ user }) => {
  const [activeSection, setActiveSection] = useState('student');
  const [searchQuery, setSearchQuery] = useState('');
  const [openTopics, setOpenTopics] = useState({});

  let currentUser = user;
  if (!currentUser) {
    try {
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        currentUser = JSON.parse(storedUser);
      }
    } catch (e) {
      console.error('Error parsing user from localStorage:', e);
    }
  }
  const isStudent = currentUser?.role === 'student';

  const guideData = [
    // 🧑‍🎓 Студентам
    {
      id: "student-courses",
      category: "student",
      categoryLabel: "🧑‍🎓 Студентам",
      title: "📖 Проходження навчальних курсів",
      icon: <BookOpen size={18} />,
      summary: "Як крок за кроком проходити теоретичні модулі та вивчати матеріали.",
      content: (
        <div className="ug-topic-body">
          <p>Кабінет навчання розділений на логічні етапи (наприклад, <strong>«Новий співробітник»</strong>, <strong>«Тести після 1 місяця»</strong>). Це допомагає послідовно освоювати стандарти закладу.</p>
          <div className="ug-substep">
            <h4>Як навчатися:</h4>
            <ul>
              <li>Перейдіть у вкладку <strong>«Курси»</strong> у вашому особисту кабінеті.</li>
              <li>Оберіть доступний курс та натисніть кнопку <strong>«Почати навчання»</strong> або <strong>«Продовжити»</strong>.</li>
              <li>Курс складається з кроків, які перемикаються стрілками внизу екрану.</li>
            </ul>
          </div>
          <div className="ug-substep">
            <h4>Формати матеріалів у кроках:</h4>
            <ul>
              <li>📝 <strong>Текст та списки:</strong> стандарти обслуговування, правила етикету, описи страв.</li>
              <li>🖼️ <strong>Зображення:</strong> фото правильної презентації страв, схеми нарізки, зовнішній вигляд форми.</li>
              <li>🎥 <strong>Відео-матеріали:</strong> відео-уроки з сервірування чи приготування напоїв. Ви можете керувати плеєром безпосередньо у кроці.</li>
              <li>📂 <strong>PDF-документи:</strong> меню, карти вин, детальні стандарти. Їх можна відкрити на весь екран або завантажити на пристрій.</li>
            </ul>
          </div>
          <div className="ug-alert ug-alert-info">
            <span className="ug-alert-icon">💡</span>
            <div className="ug-alert-text">
              <strong>Збереження прогресу:</strong> Платформа автоматично запам'ятовує крок, на якому ви зупинилися. Якщо ви закриєте вкладку, наступного разу навчання розпочнеться саме з цього кроку.
            </div>
          </div>
        </div>
      )
    },
    {
      id: "student-tests",
      category: "student",
      categoryLabel: "🧑‍🎓 Студентам",
      title: "🎮 Виконання практичних завдань",
      icon: <Gamepad2 size={18} />,
      summary: "Різновиди інтерактивних тестів, симулятори діалогу та сервірування.",
      content: (
        <div className="ug-topic-body">
          <p>У вкладці <strong>«Завдання»</strong> відображаються контрольні тест-модулі, призначені вам менеджером. Платформа містить кілька унікальних інтерактивних інструментів перевірки знань:</p>
          <div className="ug-substep">
            <h4>Типи інтерактивних симуляцій:</h4>
            <ul>
              <li>🍽️ <strong>Сервірування столу (Virtual Desk):</strong> Інтерактивна розстановка посуду та приборів. Перетягніть елементи (виделки, ножі, тарілки, келихи) на правильні місця на столі. Система підсвічує зеленою чи червоною рамкою правильність встановлення з точністю до пікселів.</li>
              <li>📝 <strong>Квізи:</strong> Стандартні текстові питання з вибором однієї або кількох відповідей. Деякі запитання містять зображення страв або аудіо-записи.</li>
              <li>💬 <strong>Симулятор діалогу (Roleplay):</strong> Рольова гра з гостем. Читайте репліки гостя, обирайте найкращий варіант відповіді офіціанта. Кожна відповідь впливає на лояльність гостя та бали сервісу.</li>
              <li>🗣️ <strong>Голосовий AI-тренажер:</strong> Тестування на знання меню та презентацію страв. Увімкніть мікрофон та проговоріть відповідь. AI-модель аналізує вашу мову на наявність ключових слів та повноту презентації.</li>
              <li>🍕 <strong>Конструктор рецептів:</strong> Перетягуйте інгредієнти у правильному порядку на тісто (наприклад, для перевірки знань рецептів піци).</li>
              <li>🍷 <strong>Винні пари:</strong> Завдання на підбір ідеального алкогольного супроводу до страв з меню ресторану.</li>
            </ul>
          </div>
        </div>
      )
    },
    {
      id: "student-gamification",
      category: "student",
      categoryLabel: "🧑‍🎓 Студентам",
      title: "🏆 Гейміфікація, XP та Лідерборд",
      icon: <Trophy size={18} />,
      summary: "Накопичення XP (досвіду), щоденний стрік активності та унікальні значки.",
      content: (
        <div className="ug-topic-body">
          <p>Навчання на платформі побудоване на ігрових механіках. За кожну активність ви отримуєте досвід (XP) та відкриваєте досягнення.</p>
          <div className="ug-substep">
            <h4>Формула нарахування XP:</h4>
            <p>За кожне проходження тесту нараховується: <strong>10 XP за спробу</strong> (нараховується за перші 3 спроби, щоб уникнути зловживань) + <strong>відсоток успішності</strong> (до 100 XP за найкраще проходження). Максимально за один тест можна отримати 110 XP.</p>
          </div>
          <div className="ug-substep">
            <h4>Серія активності (Streak):</h4>
            <p>Проходьте принаймні один тест щодня, щоб збільшувати свій щоденний показник активності (стрік), який відображається вогником 🔥. Якщо пропустити день, серія скинеться.</p>
          </div>
          <div className="ug-substep">
            <h4>Унікальні значки (Badges):</h4>
            <ul>
              <li>🎖️ <strong>Перший крок:</strong> пройти першу спробу будь-якого тесту.</li>
              <li>🏆 <strong>Профі:</strong> успішно завершити більше 10 тестів.</li>
              <li>⭐ <strong>Відмінник:</strong> скласти хоча б один тест на 100%.</li>
              <li>🔥 <strong>Марафонець:</strong> утримувати серію активності протягом 3 днів поспіль.</li>
              <li>🍷 <strong>Сомельє:</strong> успішно скласти тест на винні пари.</li>
              <li>🗣️ <strong>Оратор:</strong> успішно пройти голосовий AI-тренажер.</li>
            </ul>
          </div>
          <div className="ug-alert ug-alert-success">
            <span className="ug-alert-icon">🥇</span>
            <div className="ug-alert-text">
              <strong>Лідерборд:</strong> Порівнюйте свої результати з колегами! Ви можете відфільтрувати таблицю лідерів за своїм містом чи рестораном, щоб бачити свій прогрес у реальному часі.
            </div>
          </div>
        </div>
      )
    },
    {
      id: "student-certificates",
      category: "student",
      categoryLabel: "🧑‍🎓 Студентам",
      title: "🏅 Отримання та перевірка сертифікатів",
      icon: <Award size={18} />,
      summary: "Генерація верифікованих дипломів та їх перевірка за допомогою QR-кодів.",
      content: (
        <div className="ug-topic-body">
          <p>Успішне складання комплексних або фінальних іспитів підтверджується офіційним сертифікатом платформи.</p>
          <div className="ug-substep">
            <h4>Переваги сертифікатів:</h4>
            <ul>
              <li>📄 <strong>PDF-завантаження:</strong> Сертифікат можна зберегти на пристрій або роздрукувати.</li>
              <li>🛡️ <strong>QR-код верифікації:</strong> Кожен сертифікат містить унікальний QR-код, який веде на публічну сторінку перевірки. Будь-який менеджер може відсканувати його, щоб підтвердити справжність ваших знань.</li>
            </ul>
          </div>
        </div>
      )
    },
    {
      id: "student-store",
      category: "student",
      categoryLabel: "🧑‍🎓 Студентам",
      title: "🏪 Внутрішній магазин подарунків (ServIQ Store)",
      icon: <ShoppingBag size={18} />,
      summary: "Як заробляти ServIQ Coins та купувати за них подарунки, мерч і бонуси.",
      content: (
        <div className="ug-topic-body">
          <p>На нашій платформі діє внутрішня крамниця подарунків, де ви можете обмінювати накопичені бали досвіду та досягнення на реальні й віртуальні призи!</p>
          <div className="ug-substep">
            <h4>Як заробляти ServIQ Coins (монети):</h4>
            <ul>
              <li>🪙 <strong>За накопичення досвіду:</strong> Ви автоматично отримуєте <strong>1 Coin</strong> за кожні <strong>10 XP</strong>, отриманих під час складання будь-яких тестів.</li>
              <li>📖 <strong>За успішне закінчення курсів:</strong> При повному завершенні будь-якого навчального курсу на ваш баланс додатково нараховується <strong>25 Coins</strong>.</li>
              <li>🎖️ <strong>За досягнення (баджі):</strong> Кожне відкрите досягнення (наприклад, «Відмінник», «Марафонець», «Швидкісний») додає до вашого гаманця ще <strong>5 Coins</strong>.</li>
            </ul>
          </div>
          <div className="ug-substep">
            <h4>Як купувати товари в магазині:</h4>
            <ol>
              <li>Перейдіть у вкладку <strong>«Магазин»</strong> у вашому особистому кабінеті.</li>
              <li>Виберіть товар, що вам сподобався (одяг, кава, сертифікати тощо) та натисніть кнопку <strong>«Купити»</strong>. Товари підбираються автоматично під ваше місто.</li>
              <li>Якщо у вас достатньо монет, підтвердіть покупку у вікні, що з'явиться. Опціонально ви можете додати коментар (наприклад, вказати потрібний розмір футболки).</li>
              <li>Після списання монет замовлення з'явиться у вашій <strong>«Історії покупок»</strong> зі статусом <em>«Очікує видачі»</em>.</li>
              <li>Зверніться до свого тренера чи адміністратора, щоб отримати ваш фізичний або віртуальний подарунок. Після передачі менеджер відзначить замовлення як виконане.</li>
            </ol>
          </div>
          <div className="ug-alert ug-alert-info">
            <span className="ug-alert-icon">🔄</span>
            <div className="ug-alert-text">
              <strong>Повернення монет у разі скасування:</strong> Якщо замовлення буде скасовано менеджером (наприклад, якщо товару немає на складі), ваші монети миттєво повернуться на баланс автоматично!
            </div>
          </div>
        </div>
      )
    },

    // 👩‍🏫 Тренерам (4 детальних розділи)
    {
      id: "trainer-isolation",
      category: "trainer",
      categoryLabel: "👩‍🏫 Тренерам",
      title: "🛡️ Локальна ізоляція та права тренера",
      icon: <MapPin size={18} />,
      summary: "Обмеження доступу тренера виключно своїм містом, рестораном та доступними йому інструментами.",
      content: (
        <div className="ug-topic-body">
          <p>Роль тренера (trainer) спеціально розроблена для шеф-офіціантів, адміністраторів залів та локальних менеджерів. Вона фокусує вашу роботу виключно на співробітниках вашої філії та захищає глобальні налаштування від випадкових змін.</p>
          <div className="ug-substep">
            <h4>1. Межі прав та доступні розділи:</h4>
            <ul>
              <li><strong>Панель (Dashboard):</strong> Відображає зведену статистику навчання (активні учні, середня успішність, кількість проходжень) лише для вашого міста.</li>
              <li><strong>Гравці (Players):</strong> Ви бачите список співробітників, які працюють виключно у вашому місті. Ви не можете переглядати чи редагувати профілі з інших міст.</li>
              <li><strong>Навчання (Learning):</strong> Відображає списки наявних курсів, бібліотеку медіафайлів та конструктори у режимі «лише для читання» (як каталог доступних матеріалів). Ви можете бачити, які шаблони існують, але не можете їх редагувати чи видаляти.</li>
              <li><strong>Як користуватися (User Guide):</strong> Повний доступ до інструкцій для студентів та тренерів для спрощення Onboarding-процесу.</li>
            </ul>
          </div>
          <div className="ug-substep">
            <h4>2. Автоматична фільтрація даних:</h4>
            <p>Уся аналітика, лідерборди (рейтинги), звіти про проходження та статистика автоматично ізолюються під ваше місто. Система автоматично підставляє назву вашого міста при перегляді будь-каких звітів чи списків.</p>
          </div>
          <div className="ug-alert ug-alert-info">
            <span className="ug-alert-icon">🛡️</span>
            <div className="ug-alert-text">
              <strong>Безпека бази даних:</strong> Тренери не мають доступу до сторінок обслуговування системи (Settings), управління містами (Cities), підрозділами (Departments), загальним меню страв (Menu Management) та користувачами адмін-панелі (Users). Ці розділи приховані на рівні інтерфейсу та заблоковані на рівні API-запитів.
            </div>
          </div>
        </div>
      )
    },
    {
      id: "trainer-players",
      category: "trainer",
      categoryLabel: "👩‍🏫 Тренерам",
      title: "👥 Додавання гравців та реєстраційні лінки",
      icon: <Users size={18} />,
      summary: "Покрокове ручне створення нових співробітників та використання автоматичних лінків самореєстрації.",
      content: (
        <div className="ug-topic-body">
          <p>Щоб працівники отримали доступ до кабінету навчання, їх потрібно додати до бази даних. Платформа пропонує два зручні способи залучення персоналу:</p>
          <div className="ug-substep">
            <h4>Спосіб 1: Ручне додавання співробітника (для поштучного додавання)</h4>
            <ol>
              <li>Перейдіть до розділу <strong>«Гравці»</strong> у меню сайдбару.</li>
              <li>Натисніть кнопку <strong>«➕ Додати гравця»</strong> (доступна у верхній панелі).</li>
              <li>Заповніть обов'язкові поля форми:
                <ul>
                  <li><strong>Ім'я та Прізвище:</strong> реальні дані співробітника для генерації сертифікатів.</li>
                  <li><strong>Логін (Username):</strong> унікальне ім'я для входу (рекомендований формат: <code>ім'я.прізвище</code> латиницею).</li>
                  <li><strong>Пароль:</strong> тимчасовий пароль для першого входу (мінімум 6 символів). Співробітник зможе змінити його пізніше.</li>
                  <li><strong>Посада та Підрозділ:</strong> виберіть зі списку (наприклад, Офіціант, Бармен).</li>
                  <li><strong>Рівень гравця:</strong> виберіть кваліфікаційну категорію (<em>Стажер, Молодший, Середній, Старший</em>). Рівень впливає на доступність деяких курсів.</li>
                  <li><strong>📍 Місто:</strong> буде автоматично заблоковано на вашому поточному місті, щоб уникнути помилок прикріплення.</li>
                </ul>
              </li>
              <li>Натисніть <strong>«Зберегти»</strong>. Новий гравець з'явиться у вашій таблиці, і ви одразу зможете призначити йому перші завдання.</li>
            </ol>
          </div>
          <div className="ug-substep">
            <h4>Спосіб 2: Onboarding-посилання для самореєстрації (Рекомендовано для груп)</h4>
            <ol>
              <li>У розділі <strong>«Гравці»</strong> натисніть кнопку <strong>«🔗 Запросити гравця»</strong>.</li>
              <li>Встановіть параметри для нових користувачів:
                <ul>
                  <li><strong>Підрозділ та Посада:</strong> група, куди автоматично потраплять зареєстровані (наприклад, Офіціанти).</li>
                  <li><strong>Рівень:</strong> стартовий рівень (наприклад, <em>Стажер</em>).</li>
                </ul>
              </li>
              <li>Натисніть кнопку <strong>«Згенерувати посилання»</strong>.</li>
              <li>Натисніть кнопку <strong>«Скопіювати»</strong> та надішліть це посилання у робочий чат або групу вашої команди.</li>
              <li>При переході за цим посиланням новий співробітник самостійно вкаже своє Ім'я, Прізвище, створить логін і пароль. Його акаунт миттєво створиться з прив'язкою до вашого міста та обраної посади.</li>
            </ol>
          </div>
          <div className="ug-alert ug-alert-warning">
            <span className="ug-alert-icon">⚠️</span>
            <div className="ug-alert-text">
              <strong>Вирішення проблем із входом:</strong> Якщо гравець забув свій пароль, знайдіть його в таблиці «Гравці», натисніть на його ім'я для переходу в профіль, натисніть кнопку <strong>«Редагувати профіль»</strong> і введіть новий пароль у відповідне поле. Логін (username) змінити після створення не можна.
            </div>
          </div>
        </div>
      )
    },
    {
      id: "trainer-assignments",
      category: "trainer",
      categoryLabel: "👩‍🏫 Тренерам",
      title: "🎯 Швидке призначення завдань та курсів",
      icon: <ClipboardList size={18} />,
      summary: "Як призначати тести окремим гравцям, здійснювати масові призначення та скасовувати активні завдання.",
      content: (
        <div className="ug-topic-body">
          <p>Для керування траєкторією навчання ви можете призначати теоретичні курси або окремі симулятори трьома методами:</p>
          <div className="ug-substep">
            <h4>1. Призначення з таблиці «Гравці» (в один клік):</h4>
            <p>У таблиці навпроти імені кожного співробітника є кнопка <strong>«Призначити»</strong>. Натиснувши її, ви побачити модальне вікно, де зможете обрати потрібний курс або окрему практичну симуляцію (наприклад, квіз із меню чи Virtual Desk сервірування).</p>
          </div>
          <div className="ug-substep">
            <h4>2. Призначення з профілю студента (індивідуальний аналіз):</h4>
            <p>Під час перегляду детального профілю студента (де видно його слабкі зони), у верхньому правому кутку екрана доступна кнопка <strong>«Призначити завдання»</strong>. Це дозволяє оперативно додати тести на теми, які потребують відпрацювання.</p>
          </div>
          <div className="ug-substep">
            <h4>3. Масові призначення через Менеджер завдань (для груп):</h4>
            <ol>
              <li>Перейдіть до розділу <strong>«Гравці»</strong> -> вкладка <strong>«Менеджер призначень»</strong>.</li>
              <li>Виберіть тип ресурсу: <em>Курс, Квіз, Гра-симулятор, Сервірування столу, Комплексний іспит тощо</em>.</li>
              <li>Виберіть конкретний шаблон зі списку.</li>
              <li>Вкажіть адресатів:
                <ul>
                  <li><strong>Індивідуально:</strong> виберіть конкретного студента зі списку.</li>
                  <li><strong>Групове призначення:</strong> призначити усім співробітникам певної Посади (наприклад, усім Офіціантам вашого міста).</li>
                </ul>
              </li>
              <li>Встановіть тип лінка: <strong>Багаторазовий (Multi-use)</strong> — учень може проходити його безліч разів для тренування; або <strong>Одноразовий (Single-use)</strong> — лінк згорить одразу після першого проходження (використовується для контрольних іспитів).</li>
              <li>Натисніть <strong>«Створити призначення»</strong>. Завдання миттєво з'явиться в кабінетах обраних співробітників.</li>
            </ol>
          </div>
          <div className="ug-substep">
            <h4>Скасування призначень:</h4>
            <p>If завдання було призначено помилково, перейдіть у вкладку <strong>«Менеджер призначень»</strong>, знайдіть потрібне призначення у списку активних внизу сторінки та натисніть червону кнопку <strong>«Скасувати / Видалити»</strong>.</p>
          </div>
        </div>
      )
    },
    {
      id: "trainer-analytics",
      category: "trainer",
      categoryLabel: "👩‍🏫 Тренерам",
      title: "📊 Аналітика результатів та AI-діагностика",
      icon: <Activity size={18} />,
      summary: "Як читати статистику проходжень, аналізувати слабкі зони та застосовувати підказки ШІ у навчанні.",
      content: (
        <div className="ug-topic-body">
          <p>Система автоматично збирає результати проходження всіх тестів та надає тренеру готові інструменти для коучингу:</p>
          <div className="ug-substep">
            <h4>1. Аналіз профілю гравця та деталізація спроб:</h4>
            <ul>
              <li>У профілі студента відображається загальна статистика: загальний відсоток успішності, кількість пройдених тестів, накопичений досвід (XP), badges та діюча серія активності.</li>
              <li>У блоці <strong>«Історія проходжень»</strong> клікніть на будь-яку спробу тесту, щоб розгорнути детальну інформацію: які саме відповіді вибрав учень, у яких питаннях припустився помилок, яку схему посуду склав на столі, або який вибір зробив у діалозі з гостем.</li>
            </ul>
          </div>
          <div className="ug-substep">
            <h4>2. Блок «Слабкі зони»:</h4>
            <p>Платформа автоматично аналізує всі помилки учня та групує їх за темами (наприклад, <em>«Рецептура барних напоїв»</em>, <em>«Стандарти зустрічі гостей»</em>, <em>«Червоні вина»</em>). Теми, у яких середній бал становить менше 80%, підсвічуються червоним кольором. Це підказує тренеру, який саме матеріал потрібно додатково розібрати на практиці.</p>
          </div>
          <div className="ug-substep">
            <h4>3. AI-рекомендації (Розумний асистент):</h4>
            <p>У профілі кожного студента вбудована AI-модель аналізує загальну картину успішності гравця. ШІ генерує текстову рекомендацію для тренера, яка вказує на приховані закономірності (наприклад, ШІ може виявити, що учень відмінно орієнтується у квізах, але губиться у стресових ситуаціях у діалоговому симуляторі, та порекомендує провести з ним практичне відпрацювання сервісу в залі).</p>
          </div>
        </div>
      )
    },
    {
      id: "trainer-store",
      category: "trainer",
      categoryLabel: "👩‍🏫 Тренерам",
      title: "🏪 Обробка замовлень та видача призів",
      icon: <ShoppingBag size={18} />,
      summary: "Як відстежувати замовлення студентів, підтверджувати видачу та скасовувати замовлення.",
      content: (
        <div className="ug-topic-body">
          <p>Коли студенти купують товари у магазині, тренери отримують сповіщення в Telegram та бачать нові замовлення в адмін-панелі для видачі.</p>
          <div className="ug-substep">
            <h4>Процедура видачі призів:</h4>
            <ol>
              <li>Перейдіть до розділу <strong>«Магазин»</strong> у сайдбарі адмін-панелі. На вкладці <strong>«Замовлення»</strong> ви побачите список активних покупок гравців вашого міста.</li>
              <li>Зверніть увагу на <strong>Коментар гравця</strong> (там може бути зазначено колір чи розмір речі).</li>
              <li>Підготуйте та передайте фізичний приз працівнику.</li>
              <li>Після успішного вручення натисніть зелену кнопку <strong>«Видати»</strong> навпроти замовлення. Його статус зміниться на «Видано».</li>
            </ol>
          </div>
          <div className="ug-substep">
            <h4>Скасування замовлень та повернення:</h4>
            <p>Якщо товару немає на складі, або працівник оформив покупку помилково, натисніть червону кнопку <strong>«Скасувати»</strong>. Монети за покупку автоматично та миттєво повернуться на баланс студента, а резервована одиниця товару повернеться на склад.</p>
          </div>
          <div className="ug-substep">
            <h4>📊 Як учні накопичують монети та ціноутворення:</h4>
            <p>Щоб ви розуміли реальну швидкість накопичення валюти учнями для видачі призів та консультування:</p>
            <ul>
              <li><strong>Формула заробітку Coins учнями:</strong>
                <ul>
                  <li>🪙 <strong>1 Coin</strong> за кожні <strong>10 XP</strong> (досвіду). За один повністю складений тест на 100% зі спробами можна отримати максимум <strong>11–13 Coins</strong> (100 XP за найкраще проходження + до 30 XP за перші спроби).</li>
                  <li>📖 <strong>25 Coins</strong> за повне проходження будь-якого курсу.</li>
                  <li>🎖️ <strong>5 Coins</strong> за кожне отримане досягнення (бадж).</li>
                </ul>
              </li>
              <li><strong>Орієнтири швидкості накопичення:</strong> щоб зібрати <strong>100 Coins</strong>, учневі потрібно пройти приблизно 1–2 курси та скласти 5–7 супутніх тестів з високим балом (це орієнтовно 1–2 тижні регулярного навчання).</li>
              <li><strong>Рекомендована градація цін:</strong>
                <ul>
                  <li>🥤 <strong>Дрібні заохочення (20–50 Coins):</strong> кава, круасан, фірмова ручка чи стікерпак (легка мета на перші дні).</li>
                  <li>👕 <strong>Середні призи (100–250 Coins):</strong> чашка, фірмова футболка, блокнот-планер, кепка (вимагає зусиль протягом 2–3 тижнів).</li>
                  <li>🧥 <strong>Цінні нагороди (500–1000 Coins):</strong> брендоване худі, якісний рюкзак, вечеря чи додатковий вихідний (серйозна мета за повне завершення Onboarding-програми).</li>
                </ul>
              </li>
            </ul>
          </div>
        </div>
      )
    },

    // ⚙️ ... (8 детальних розділів)
    {
      id: "admin-course-builder",
      category: "admin",
      categoryLabel: "⚙️ Адміністраторам",
      title: "📚 Створення курсів та етапів навчання (Course Manager)",
      icon: <FolderOpen size={18} />,
      summary: "Як будувати послідовні навчальні програми, створювати етапи навчання та підключати сертифікати.",
      content: (
        <div className="ug-topic-body">
          <p>Конструктор курсів (Course Manager) об'єднує теоретичні матеріали та практичні тестування у єдину логічну лінійку навчання.</p>
          <div className="ug-substep">
            <h4>Крок 1: Базові налаштування курсу</h4>
            <ol>
              <li>Перейдіть до розділу <strong>«Навчання»</strong> -> вкладка <strong>«Курси навчання»</strong> та натисніть <strong>«Створити курс»</strong>.</li>
              <li>Вкажіть <strong>Назву курсу *</strong> та <strong>Опис курсу</strong>.</li>
              <li>У блоці <strong>Обкладинка (URL або файл)</strong> вставте пряме посилання на зображення або натисніть кнопку <strong>📁 Файл</strong> для завантаження картинки з комп'ютера. Після цього з'явиться зручна мініатюра попереднього перегляду з можливістю швидкого видалення 🗑️.</li>
              <li>Налаштуйте видимість: виберіть конкретне 📍 <strong>Місто</strong> (або залиште порожнім для загального доступу).</li>
              <li>Прив'яжіть 📂 <strong>Етап навчання</strong> (наприклад, <em>«Стажування офіціантів»</em>). Якщо потрібного етапу немає, натисніть кнопку <strong>⚙️ Налаштувати етапи</strong>, додайте новий етап (назва, опис, порядок сортування) та збережіть його.</li>
              <li>Виберіть 🎓 <strong>Сертифікат про завершення</strong>. Якщо обрано шаблон сертифіката, після успішного проходження останнього кроку курсу студент автоматично отримає верифікований PDF-диплом у своєму кабінеті.</li>
              <li>Встановіть галочку <strong>Активний курс</strong> для публікації.</li>
            </ol>
          </div>
          <div className="ug-substep">
            <h4>Крок 2: Додавання модулів та кроків навчання</h4>
            <ol>
              <li>Натисніть кнопку <strong>➕ Додати модуль</strong>. Вкажіть заголовок та опис модуля (наприклад, <em>«Модуль 1: Меню кухні»</em>).</li>
              <li>Всередині модуля натисніть <strong>➕ Додати крок</strong>.</li>
              <li>Оберіть <strong>Тип кроку</strong> та заповніть його:
                <ul>
                  <li><strong>📖 Текстовий урок:</strong> відкриває текстовий WYSIWYG-редактор. Введіть text уроку, відформатуйте його заголовками, списками чи жирним шрифтом.</li>
                  <li><strong>🎥 Відео-лекція:</strong> введіть URL-посилання на відео (підтримуються прямі посилання на MP4 файли з вашої медіабібліотеки або посилання на YouTube-відео).</li>
                  <li><strong>📄 PDF-матеріал:</strong> вкажіть посилання на PDF. Ви можете натиснути кнопку <strong>📂 З бібліотеки</strong>, щоб обрати раніше завантажений PDF-файл із медіабібліотеки.</li>
                  <li><strong>Практичний крок (Quiz, Desk, Game, Voice, Pizza, Complex):</strong> оберіть тип практичного тесту та виберіть конкретний шаблон зі списку доступних. Вкажіть <strong>Прохідний бал (%)</strong> (наприклад, 85%). Учень не зможе перейти до наступного кроку курсу, поки не складе цей тест на вказаний бал.</li>
                </ul>
              </li>
              <li>Ви можете міняти порядок кроків та модулів за допомогою стрілок <strong>▲ / ▼</strong>.</li>
              <li>Натисніть кнопку <strong>💾 Створити курс</strong> внизу сторінки для збереження.</li>
            </ol>
          </div>
        </div>
      )
    },
    {
      id: "admin-quiz-builder",
      category: "admin",
      categoryLabel: "⚙️ Адміністраторам",
      title: "📝 Конструктор квізів та імпорт з Excel (Quiz Builder)",
      icon: <ClipboardList size={18} />,
      summary: "Створення текстових та мультимедійних квізів вручну або через масове завантаження Excel файлів.",
      content: (
        <div className="ug-topic-body">
          <p>Quiz Builder дозволяє гнучко налаштовувати теоретичні тести. Підтримуються питання з одиночним та множинним вибором, додавання медіа та автоматичне перемішування.</p>
          <div className="ug-substep">
            <h4>Крок 1: Налаштування параметрів тесту</h4>
            <ol>
              <li>Перейдіть до меню <strong>«Конструктори»</strong> -> картка <strong>«Конструктор квізів»</strong>.</li>
              <li>Введіть <strong>Назву квізу</strong> та його опис.</li>
              <li>Встановіть <strong>Ліміт часу (хвилин)</strong>. Значення <code>0</code> означає безлімітний час.</li>
              <li>Вкажіть <strong>Прохідний бал (%)</strong> (за замовчуванням 80%).</li>
              <li>Оберіть місто для обмеження видимого доступу.</li>
              <li>Увімкніть опции <strong>Випадковий порядок варіантів</strong> та <strong>Випадковий порядок питань</strong>, щоб захистити тест від списування.</li>
            </ol>
          </div>
          <div className="ug-substep">
            <h4>Крок 2: Наповнення питаннями</h4>
            <ul>
              <li><strong>Ручне додавання:</strong> Натисніть <strong>➕ Додати питання</strong>. Введіть текст питання. Опціонально додайте посилання на зображення або аудіо у блок «Медіа». Натисніть <strong>➕ Додати варіант</strong> для створення відповідей. Позначте галочкою правильний варіант (або кілька варіантів для множинного вибору). Введіть пояснення помилки.</li>
              <li><strong>Масовий імпорт з Excel (Рекомендовано):</strong>
                <ol>
                  <li>Натисніть кнопку <strong>«Завантажити шаблон Excel»</strong>.</li>
                  <li>Відкрийте завантажений файл. Стовпець <code>A</code> — текст питання. Стовпці <code>B-G</code> — варіанти відповідей. Стовпець <code>H</code> — пояснення помилки.</li>
                  <li><strong>Важливо:</strong> У варіантах відповідей поставте символ зірочки <code>*</code> перед правильним варіантом (наприклад, <code>*Карбонара</code>). Якщо правильних варіантів кілька, позначте зірочками всі правильні варіанти.</li>
                  <li>Збережіть файл, перетягніть його у зону завантаження конструктора або натисніть <strong>«Імпортувати Excel»</strong>. Усі питання миттєво з'являться в редакторі.</li>
                </ol>
              </li>
            </ul>
          </div>
          <div className="ug-substep">
            <h4>Крок 3: Збереження квізу</h4>
            <p>Натисніть кнопку <strong>💾 Створити квіз</strong> (або <strong>💾 Зберегти зміни</strong>) вгорі або внизу сторінки.</p>
          </div>
        </div>
      )
    },
    {
      id: "admin-virtualdesk-builder",
      category: "admin",
      categoryLabel: "⚙️ Адміністраторам",
      title: "🍽️ Конструктор сервірування столів (Virtual Desk)",
      icon: <Layers size={18} />,
      summary: "Створення шаблонів розстановки посуду, налаштування допусків точності та рівнів накладання елементів.",
      content: (
        <div className="ug-topic-body">
          <p>Virtual Desk Builder — це візуальний графічний інструмент на базі HTML5 Canvas для налаштування схем сервірування столів.</p>
          <div className="ug-substep">
            <h4>Крок 1: Завантаження фонового креслення столу</h4>
            <ol>
              <li>У правому сайдбарі знайдіть блок <strong>«Розмальовки»</strong> (фонові схеми столів).</li>
              <li>Натисніть кнопку <strong>Завантажити</strong>, оберіть файл схеми столу (рекомендований розмір 1200x800px, формат PNG) та збережіть його.</li>
              <li>Клікніть на завантажену розмальовку у списку, щоб встановити її як фонову підкладку столу.</li>
            </ol>
          </div>
          <div className="ug-substep">
            <h4>Крок 2: Розміщення еталонного посуду</h4>
            <ol>
              <li>Оберіть категорію посуду у лівій панелі (Тарілки, Прибори, Скло, Допоміжні предмети).</li>
              <li>Клікніть на потрібний предмет, і він з'явиться на полотні. Предмети автоматично прилипають до сітки (grid snapping) з кроком 10 пікселів.</li>
              <li>Налаштуйте властивості виділеного предмета:
                <ul>
                  <li><strong>Позиціонування:</strong> перетягніть мишкою або стрілками клавіатури у точне правильне положення.</li>
                  <li><strong>Обертання:</strong> введіть кут обертання в градусах у правому сайдбарі (або використовуйте іконку обертання на самому предметі).</li>
                  <li><strong>Розмір:</strong> змініть ширину та висоту у пікселях, щоб посуд відповідав масштабу столу.</li>
                  <li><strong>Порядок шарів (zIndex):</strong> використовуйте кнопки <strong>«Вище» / «Нижче»</strong> для правильного нашарування (наприклад, тарілка для хліба має лежати під пиріжковим ножем).</li>
                </ul>
              </li>
            </ol>
          </div>
          <div className="ug-substep">
            <h4>Крок 3: Налаштування точності та збереження тесту</h4>
            <ol>
              <li>Натисніть кнопку <strong>«Зберегти шаблон»</strong> у верхньому кутку.</li>
              <li>Заповніть форму:
                <ul>
                  <li><strong>Назва шаблону:</strong> зрозуміле ім'я (наприклад, <em>«Сервірування вечері на 2 персони»</em>).</li>
                  <li><strong>Опис завдання:</strong> чітка інструкція для студента (що саме він має зробити).</li>
                  <li><strong>📍 Місто:</strong> виберіть місто доступу.</li>
                  <li><strong>Допуск (Tolerance) у пікселях:</strong> радіус дозволеної похибки (за замовчуванням 50 пікселів). Якщо студент поставить предмет у межах 50px від вашої еталонної координати — система зарахує відповідь як правильну. Для строгих іспитів зменшуйте допуск до 25-30px.</li>
                </ul>
              </li>
              <li>Натисніть <strong>«Зберегти»</strong>. Шаблон готовий до призначення.</li>
            </ol>
          </div>
        </div>
      )
    },
    {
      id: "admin-dialog-builder",
      category: "admin",
      categoryLabel: "⚙️ Адміністраторам",
      title: "💬 Конструктор діалогів та ElevenLabs Voice (Visual Game Builder)",
      icon: <Mic size={18} />,
      summary: "Як створювати інтерактивні сценарії розмов, з'єднувати вузли діалогу та генерувати голосову озвучку AI.",
      content: (
        <div className="ug-topic-body">
          <p>Visual Game Builder дозволяє проектувати інтерактивні симуляції обслуговування гостей у форматі блок-схеми діалогу.</p>
          <div className="ug-substep">
            <h4>Крок 1: Реєстрація Персонажів сценарію</h4>
            <ol>
              <li>Перейдіть на вкладку <strong>«Персонажі»</strong> у верхній панелі конструктора.</li>
              <li>Натисніть <strong>➕ Додати персонажа</strong>.</li>
              <li>Вкажіть ім'я персонажа (наприклад, <em>«Гість Дмитро»</em>), виберіть емодзі-аватар, задайте колір підсвічування тексту та коротко опишіть його характер. Збережіть зміни.</li>
            </ol>
          </div>
          <div className="ug-substep">
            <h4>Крок 2: Збірка графу діалогу на Канвасі</h4>
            <ol>
              <li>Перейдіть на вкладку <strong>«Канвас»</strong>.</li>
              <li>Натисніть кнопку <strong>➕ Вузол</strong> на панелі інструментів для створення нової картки репліки.</li>
              <li>Налаштуйте вузол:
                <ul>
                  <li>Виберіть дійову особу (Персонажа або Диктора).</li>
                  <li>Введіть <strong>Текст сцени</strong> (те, що говорить гість або описує диктор).</li>
                  <li>Позначте найперший вузол діалогу кнопкою <strong>«Зробити стартовим»</strong> (він отримає позначку <code>🚀 START</code>).</li>
                </ul>
              </li>
              <li>Додайте варіанти відповідей офіціанта:
                <ul>
                  <li>Натисніть кнопку <strong>«+ Додати варіант»</strong> на картці поточного вузла.</li>
                  <li>Введіть текст відповіді у поле <strong>Текст кнопки</strong>.</li>
                  <li>Вкажіть вплив відповіді: нарахування балів лояльності або штраф (наприклад, <code>-5</code>).</li>
                  <li>З'єднуйте варіант із наступним вузлом: затисніть мишкою іконку ланцюжка <strong>🔗</strong> поруч із варіантом та проведіть стрілку до картки наступного вузла діалогу.</li>
                  <li>Якщо варіант відповіді веде до завершення сценарію: поставте галочку <strong>Перемога (isWin)</strong> та впишіть фінальний текст підсумку.</li>
                </ul>
              </li>
            </ol>
          </div>
          <div className="ug-substep">
            <h4>Крок 3: Налаштування нейронного синтезу ElevenLabs Voice</h4>
            <p>У властивостях вузла діалогу ви можете озвучити репліку гостя: виберіть модель голосу ElevenLabs зі списку та налаштуйте повзунки стабільності та чіткості вимови. При збереженні система автоматично згенерує аудіофайл і застосує нейронний голос для озвучення діалогу.</p>
          </div>
        </div>
      )
    },
    {
      id: "admin-pairing-builder",
      category: "admin",
      categoryLabel: "⚙️ Адміністраторам",
      title: "🍕 Конструктор піци (Pizza Builder)",
      icon: <Gamepad2 size={18} />,
      summary: "Доступ до бази інгредієнтів, налаштування етапів збірки піци та рецептур.",
      content: (
        <div className="ug-topic-body">
          <p>Цей конструктор об'єднує симулятор збірки піци (Pizza Simulator) та систему перевірки знань рецептур страв.</p>
          <div className="ug-substep">
            <h4>Крок 1: Вхід у конструктор</h4>
            <p>Перейдіть до розділу «Конструктори» та виберіть «🍕 Конструктор піци». Доступ відкрито для адміністраторів та менеджерів.</p>
          </div>
          <div className="ug-substep">
            <h4>Крок 2: Створення та налаштування бази інгредієнтів</h4>
            <ol>
              <li>Перейдіть до вкладки <strong>«⚙️ Тісто, Соуси, Топінги, Напої»</strong>.</li>
              <li>Виберіть тип інгредієнта для додавання:
                <ul>
                  <li><strong>🥖 Тісто (основа):</strong> тонке, пишне, житнє.</li>
                  <li><strong>🍅 Соуси:</strong> томатний, вершковий, песто.</li>
                  <li><strong>🥓 Топінги:</strong> пепероні, сир моцарела, гриби, зелень.</li>
                  <li><strong>🍷 Напої (Парування):</strong> К'янті, Шардоне, світле пиво.</li>
                </ul>
              </li>
              <li>Оберіть відповідний візуальний емодзі-значок, впишіть назву інгредієнта та натисніть кнопку <strong>➕ Додати</strong>. Інгредієнт з'явиться в базі.</li>
            </ol>
          </div>
          <div className="ug-substep">
            <h4>Крок 3: Побудова рецепту та правил перевірки</h4>
            <ol>
              <li>Перейдіть до вкладки <strong>«📝 Рецепт піци»</strong>.</li>
              <li>Введіть назву страви (наприклад, <em>«Піца Кватро Формаджі»</em>) та опис.</li>
              <li>Виберіть правильний тип тіста та правильний базовий соус.</li>
              <li>Позначте галочками інгредієнти, які студент має обов'язково розкласти на піцу.</li>
              <li>Оберіть правильний напій зі списку парування (Pairing), який найкраще пасує до цієї страви.</li>
              <li>Вкажіть час на проходження тесту (за замовчуванням 2 хвилини) та місто доступу.</li>
              <li>Натисніть кнопку <strong>💾 Зберегти рецепт</strong>. Рецепт автоматично додається у список практичних кроків у Course Manager.</li>
            </ol>
          </div>
        </div>
      )
    },
    {
      id: "admin-complex-builder",
      category: "admin",
      categoryLabel: "⚙️ Адміністраторам",
      title: "🧩 Побудова комплексних іспитів (Complex Test Builder)",
      icon: <Award size={18} />,
      summary: "Як об'єднати квізи, сервірування столу та діалогові сценарії в один великий екзаменаційний модуль.",
      content: (
        <div className="ug-topic-body">
          <p>Complex Test Builder призначений для збірки фінальних атестацій співробітників, об'єднуючи декілька раніше створених тестів різних типів у єдиний послідовний іспит.</p>
          <div className="ug-substep">
            <h4>Крок 1: Створення іспиту</h4>
            <ol>
              <li>Перейдіть до меню <strong>«Конструктори»</strong> -> картка <strong>«Комплексний тест»</strong>.</li>
              <li>Введіть назву атестації (наприклад, <em>«Фінальний іспит Офіціанта: Рівень Middle»</em>) та введіть опис для ознайомлення.</li>
              <li>Вкажіть загальний <strong>Прохідний бал (%)</strong> за весь іспит (наприклад, 90%).</li>
              <li>Оберіть місто для обмеження видимості атестації.</li>
            </ol>
          </div>
          <div className="ug-substep">
            <h4>Крок 2: Додавання та налаштування етапів іспиту</h4>
            <ol>
              <li>Натисніть кнопку <strong>➕ Додати крок</strong>.</li>
              <li>Виберіть тип тесту: <em>Квіз (Quiz), Сервірування столу (DeskTemplate) або Діалогова гра (GameScenario)</em>.</li>
              <li>У списку <strong>«Виберіть шаблон»</strong> оберіть конкретне завдання, яке ви раніше створили у відповідному конструкторі.</li>
              <li>Ви можете додавати необмежену кількість кроків. Змінюйте черговість проходження кнопками <strong>▲ / ▼</strong> на картках кроків.</li>
              <li>Натисніть кнопку <strong>💾 Зберегти комплексний тест</strong>. Після проходження такого тесту учнем система збереже результати окремо по кожному етапу та вирахує загальний середній бал.</li>
            </ol>
          </div>
        </div>
      )
    },
    {
      id: "admin-generate-links",
      category: "admin",
      categoryLabel: "⚙️ Адміністраторам",
      title: "🔗 Створення та копіювання посилань на тести/гравців",
      icon: <HelpCircle size={18} />,
      summary: "Отримання прямих лінків на завдання для вбудовування в уроки або генерація onboarding-лінків для самореєстрації.",
      content: (
        <div className="ug-topic-body">
          <p>Платформа ServIQ дозволяє копіювати та генерувати посилання для спрощення інтеграції з іншими системами та автоматичного додавання користувачів.</p>
          <div className="ug-substep">
            <h4>1. Отримання посилань на завдання (для вбудовування в уроки):</h4>
            <p>Ви можете скопіювати пряме посилання на будь-яке створене практичне завдання:</p>
            <ul>
              <li><strong>Квізи:</strong> Перейдіть до розділу «Квізи», знайдіть картку тесту та натисніть іконку посилання (ланцюжок 🔗).</li>
              <li><strong>Сервірування столів (Virtual Desk):</strong> У списку шаблонів натисніть кнопку посилання навпроти потрібної сервіровки.</li>
              <li><strong>Діалогові симулятори:</strong> У Visual Game Builder на картці сценарію натисніть круглу іконку посилання 🔗.</li>
              <li><strong>Голосові AI-тренажери:</strong> У розділі «Голосовий тренажер» натисніть кнопку копіювання посилання поруч зі сценарієм.</li>
              <li><strong>Винні пари / Конструктор рецептів:</strong> Скопіюйте посилання поруч із рецептом у Pairing Builder.</li>
              <li><strong>Комплексні тести:</strong> Скопіюйте лінк навпроти назви іспиту у Complex Test Builder.</li>
            </ul>
            <div className="ug-alert ug-alert-success">
              <span className="ug-alert-icon">💡</span>
              <div className="ug-alert-text">
                Ці посилання є загальними (багаторазовими), вони не вимагають попереднього входу користувача в особистий кабінет навчання ServIQ. Їх можна використовувати для вбудовування в <strong>AcademyOcean</strong>.
              </div>
            </div>
          </div>
          <div className="ug-substep">
            <h4>2. Onboarding-посилання для самореєстрації нових гравців:</h4>
            <ol>
              <li>Перейдіть до розділу <strong>«Гравці»</strong> у меню сайдбару.</li>
              <li>Натисніть кнопку <strong>«🔗 Запросити гравця»</strong> у верхній панелі.</li>
              <li>Виберіть <strong>Місто реєстрації</strong> зі списку (кожен, хто зареєструється за цим посиланням, буде прикріплений до цього міста).</li>
              <li>Натисніть кнопку <strong>«Згенерувати»</strong>.</li>
              <li>Скопіюйте посилання за допомогою кнопки <strong>«Скопіювати»</strong> та надішліть його співробітникам (наприклад, у Telegram-чат вашої команди).</li>
            </ol>
          </div>
        </div>
      )
    },
    {
      id: "admin-embed-academyocean",
      category: "admin",
      categoryLabel: "⚙️ Адміністраторам",
      title: "🔗 Інтеграція з AcademyOcean (Embed-коди)",
      icon: <FileText size={18} />,
      summary: "Як отримати embed-код практичних завдань та вставити їх в уроки AcademyOcean.",
      content: (
        <div className="ug-topic-body">
          <p>Платформа ServIQ повністю сумісна з кабінетами навчання <strong>AcademyOcean</strong>. Ви можете інтегрувати будь-які тести, ігри чи завдання прямо в уроки через механізм Embed-вбудовування.</p>
          <div className="ug-substep">
            <h4>Крок 1: Отримання посилання на тест</h4>
            <p>Створіть завдання (квіз, гру, сервірування чи комплексний тест) на платформі ServIQ та скопіюйте його посилання доступу.</p>
            <p><strong>Формат посилання:</strong></p>
            <pre className="ug-code-block">
              {"https://serviq.krainamriy.fun/test/[унікальний_хеш]"}
            </pre>
          </div>
          <div className="ug-substep">
            <h4>Крок 2: Вставка в уроки AcademyOcean</h4>
            <ol>
              <li>У вашому редакторі курсів AcademyOcean відкрийте потрібний урок.</li>
              <li>Додайте новий контент-блок типу <strong>«Вбудований об'єкт»</strong> (Embed / iframe).</li>
              <li>Вставте скопійовану адресу завдання.</li>
              <li>Або скористайтеся стандартним HTML-кодом вставки:</li>
            </ol>
            <pre className="ug-code-block">
              {'<iframe src="https://serviq.krainamriy.fun/test/[унікальний_хеш]" width="100%" height="650px" frameborder="0" allow="microphone"></iframe>'}
            </pre>
          </div>
          <div className="ug-alert ug-alert-info">
            <span className="ug-alert-icon">🎙️</span>
            <div className="ug-alert-text">
              <strong>Доступ до мікрофону:</strong> Якщо ви вбудовуєте <em>Голосовий AI-симулятор</em>, переконайтеся, що ваш iframe-код містить атрибут <code>allow="microphone"</code>. Це дозволить браузеру надавати доступ до мікрофона студента всередині AcademyOcean для проговорення відповідей.
            </div>
          </div>
        </div>
      )
    },
    {
      id: "admin-store-management",
      category: "admin",
      categoryLabel: "⚙️ Адміністраторам",
      title: "🏪 Управління внутрішнім магазином та залишками",
      icon: <ShoppingBag size={18} />,
      summary: "Створення та редагування товарів магазину, керування цінами, залишками та містами.",
      content: (
        <div className="ug-topic-body">
          <p>Адміністратори платформи мають можливість повноцінно керувати каталогом товарів (мерчу, призів, купонів) та бачити загальний перелік замовлень на видачу.</p>
          <div className="ug-substep">
            <h4>1. Створення нового товару для вітрини:</h4>
            <ol>
              <li>Перейдіть до розділу <strong>«Магазин»</strong> у сайдбарі адмін-панелі.</li>
              <li>Перейдіть на вкладку <strong>«Асортимент товарів»</strong> та натисніть кнопку <strong>«➕ Додати товар»</strong>.</li>
              <li>Заповніть поля:
                <ul>
                  <li><strong>Назва товару:</strong> наприклад, <em>«Фірмове худі ServIQ»</em>.</li>
                  <li><strong>Опис:</strong> детальний опис призу, умов використання або доступних кольорів.</li>
                  <li><strong>Вартість (Coins):</strong> ціна в ServIQ Coins (визначається відповідно до нашої формули XP та курсів).</li>
                  <li><strong>Кількість на складі:</strong> обмеження залишків. Якщо вказано число (наприклад, 10), при кожній покупці залишок зменшуватиметься на 1. Коли залишок досягне 0, товар автоматично зникне з вітрини. Вкажіть <code>-1</code>, якщо товар є безлімітним.</li>
                  <li><strong>📍 Місто (тільки для Superadmin):</strong> оберіть місто, до якого належить товар. Якщо залишити порожнім — товар буде доступний студентам усіх міст.</li>
                  <li><strong>Зображення:</strong> натисніть кнопку <strong>«Завантажити фото»</strong>, щоб завантажити зображення товару з комп'ютера. Воно буде автоматично оптимізовано та збережено.</li>
                  <li><strong>Статус:</strong> позначте чекбокс <strong>«Показувати товар на вітрині»</strong>, щоб активувати його відображення.</li>
                </ul>
              </li>
              <li>Натисніть <strong>«Створити»</strong> для збереження.</li>
            </ol>
          </div>
          <div className="ug-substep">
            <h4>2. Обробка замовлень на рівні Superadmin:</h4>
            <p>Суперадміністратори бачать замовлення всіх міст та можуть фільтрувати їх за статусами (Очікують видачі, Видані, Скасовані). Вони можуть видати товар гравцеві або скасувати замовлення з миттєвим автоматичним поверненням Coins на баланс користувача.</p>
          </div>
          <div className="ug-substep">
            <h4>3. Економіка монет та градація цін (ціноутворення):</h4>
            <p>Для правильного встановлення вартості створюваних товарів використовуйте наступну логіку нарахування валюти учням:</p>
            <ul>
              <li><strong>Механіка заробітку монет учнями:</strong>
                <ul>
                  <li>🪙 <strong>1 Coin</strong> нараховується за кожні <strong>10 XP</strong> (досвіду). За один повністю пройдений тест на максимальний бал учень отримує до <strong>13 Coins</strong> (100 балів за тест + до 30 балів за спроби = 130 XP).</li>
                  <li>📖 <strong>25 Coins</strong> нараховується за успішне та повне завершення будь-якого курсу.</li>
                  <li>🎖️ <strong>5 Coins</strong> додається за кожне отримане досягнення (бадж).</li>
                </ul>
              </li>
              <li><strong>Орієнтири для ціноутворення (Gradation Scale):</strong>
                <ul>
                  <li>🏷️ <strong>20 - 50 Coins (Дрібні бонуси / Швидкі призи):</strong>
                    <br /><em>Приклади:</em> кава, круасан, фірмовий стікерпак, брендована ручка.
                    <br /><em>Зусилля:</em> 2-3 дні регулярного навчання (проходження кількох тестів або 1 курсу).
                  </li>
                  <li>🏷️ <strong>100 - 250 Coins (Середні подарунки):</strong>
                    <br /><em>Приклади:</em> фірмовий кухоль (горнятко), футболка, блокнот-планер.
                    <br /><em>Зусилля:</em> 2-3 тижні активного навчання (проходження 3-5 курсів та десятка супутніх тестів).
                  </li>
                  <li>🏷️ <strong>500 - 1000+ Coins (Цінний мерч та призи):</strong>
                    <br /><em>Приклади:</em> брендоване худі/світшот, якісний рюкзак, вечеря на двох у ресторані, квиток на профільний майстер-клас.
                    <br /><em>Зусилля:</em> повне завершення великої програми навчання (наприклад, повний Onboarding стажера), підтримання тривалої серії активності (стріку) та здобуття більшості досягнень (1-2 місяці активної роботи).
                  </li>
                </ul>
              </li>
            </ul>
          </div>
        </div>
      )
    }
  ];

  const getCount = (category) => guideData.filter(t => t.category === category).length;

  const handleTopicToggle = (id) => {
    setOpenTopics(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const filteredData = guideData.filter(topic => {
    if (isStudent && topic.category !== 'student') {
      return false;
    }
    const query = searchQuery.toLowerCase().trim();
    if (!query) {
      return topic.category === activeSection;
    }
    return (
      topic.title.toLowerCase().includes(query) ||
      topic.summary.toLowerCase().includes(query) ||
      topic.categoryLabel.toLowerCase().includes(query)
    );
  });

  const isSearching = searchQuery.trim().length > 0;

  return (
    <div className="ug-container">
      <header className="ug-header">
        <div className="ug-title-group">
          <div className="ug-title-icon-wrapper">
            <HelpCircle className="ug-header-icon" size={32} />
          </div>
          <div>
            <h1>Як користуватися платформою ServIQ</h1>
            <p>Як навчатися, створювати інтерактивний контент та адмініструвати платформу</p>
          </div>
        </div>
      </header>

      {/* Interactive Search Bar */}
      <div className="ug-search-wrapper">
        <div className="ug-search-bar">
          <Search className="ug-search-icon" size={20} />
          <input 
            type="text" 
            placeholder="Пошук по довідці (наприклад: сервірування, ElevenLabs, XP, AcademyOcean, піца)..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="ug-search-input"
          />
          {searchQuery && (
            <button className="ug-search-clear" onClick={() => setSearchQuery('')}>×</button>
          )}
        </div>
      </div>

      {/* Tabs Menu (hidden during search or for regular players to focus on results) */}
      {!isSearching && !isStudent && (
        <div className="ug-tabs">
          <button 
            className={`ug-tab-btn ${activeSection === 'student' ? 'active' : ''}`}
            onClick={() => setActiveSection('student')}
          >
            <BookOpen size={16} />
            <span>🧑‍🎓 Студентам</span>
            <span className="ug-tab-badge">{getCount('student')}</span>
          </button>
          <button 
            className={`ug-tab-btn ${activeSection === 'trainer' ? 'active' : ''}`}
            onClick={() => setActiveSection('trainer')}
          >
            <Users size={16} />
            <span>👩‍🏫 Тренерам</span>
            <span className="ug-tab-badge">{getCount('trainer')}</span>
          </button>
          <button 
            className={`ug-tab-btn ${activeSection === 'admin' ? 'active' : ''}`}
            onClick={() => setActiveSection('admin')}
          >
            <Settings size={16} />
            <span>⚙️ Адміністраторам</span>
            <span className="ug-tab-badge">{getCount('admin')}</span>
          </button>
        </div>
      )}

      {/* Search status header */}
      {isSearching && (
        <div className="ug-search-results-info">
          <Sparkles size={16} className="text-yellow animate-pulse" />
          <span>Знайдено {filteredData.length} збігів за вашим запитом:</span>
        </div>
      )}

      {/* Content Accordions */}
      <div className="ug-accordion-container">
        {filteredData.length > 0 ? (
          filteredData.map((topic) => {
            const isOpen = isSearching || !!openTopics[topic.id];
            return (
              <div 
                key={topic.id} 
                className={`ug-accordion-item ${isOpen ? 'open' : ''} ${topic.category}-category`}
              >
                <div 
                  className="ug-accordion-header"
                  onClick={() => !isSearching && handleTopicToggle(topic.id)}
                  style={{ cursor: isSearching ? 'default' : 'pointer' }}
                >
                  <div className="ug-accordion-header-left">
                    <div className="ug-accordion-icon">{topic.icon}</div>
                    <div>
                      <h3 className="ug-accordion-title">{topic.title}</h3>
                      <p className="ug-accordion-summary">{topic.summary}</p>
                    </div>
                  </div>
                  <div className="ug-accordion-header-right">
                    {isSearching && (
                      <span className={`ug-role-badge badge-${topic.category}`}>
                        {topic.categoryLabel}
                      </span>
                    )}
                    {!isSearching && (
                      isOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />
                    )}
                  </div>
                </div>

                {isOpen && (
                  <div className="ug-accordion-content animate-fade-down">
                    {topic.content}
                  </div>
                )}
              </div>
            );
          })
        ) : (
          <div className="ug-no-results">
            <AlertTriangle className="text-orange" size={48} />
            <h3>Нічого не знайдено</h3>
            <p>Спробуйте змінити пошуковий запит або переглянути відповідні вкладки ролей.</p>
          </div>
        )}
      </div>

      {/* Quick Tips footer panel */}
      <footer className="ug-footer-tips">
        <div className="ug-tips-card">
          <div className="ug-tips-header">
            <Info size={18} className="text-blue" />
            <h4>Корисна порада для користувачів платформи</h4>
          </div>
          <p>
            Платформа ServIQ є повністю адаптивною. Ви можете проходити навчання, квізи та перевіряти результати як зі стаціонарних комп'ютерів, так і зі смартфонів прямо в ресторані під час зміни.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default UserGuide;
